import { DeleteFolderDialog } from '@/components/folders/delete-folder-dialog';
import { FolderDescription } from '@/components/folders/folder-description';
import { FolderDialog, type FolderDialogMode } from '@/components/folders/folder-dialog';
import { FolderEmptyState } from '@/components/folders/folder-empty-state';
import { FolderHeader } from '@/components/folders/folder-header';
import { FolderNotesPanel } from '@/components/folders/folder-notes-panel';
import { FolderSidebar } from '@/components/folders/folder-sidebar';
import { MatrixCard } from '@/components/matrix-card';
import { DeleteMatrixDialog } from '@/components/matrix-grid/delete-matrix-dialog';
import { FilterChips } from '@/components/matrix-grid/filter-chips';
import { FilteredEmptyState } from '@/components/matrix-grid/filtered-empty-state';
import { MatrixActionsMenu } from '@/components/matrix-grid/matrix-actions-menu';
import { MatrixGridToolbar } from '@/components/matrix-grid/matrix-grid-toolbar';
import { MatrixListTable } from '@/components/matrix-grid/matrix-list-table';
import { NewMatrixCard } from '@/components/new-matrix-card';
import { matricesApi } from '@/lib/api';
import { movedMessage } from '@/lib/folder-wording';
import { useFolders, useMoveMatrix } from '@/lib/folders-queries';
import {
  clearFilters,
  hasActiveFilters,
  matchesFilters,
  matchesFolder,
  matrixLinkTarget,
  matrixTitle,
  visibleMatrices,
} from '@/lib/matrix-grid-filters';
import {
  ALL_FOLDERS,
  DEFAULT_SORT_DIRECTIONS,
  type FolderSelection,
  type MatrixGridSearch,
  type MatrixGridSettings,
  type MatrixGridSort,
  NO_FOLDER,
  isFolderId,
  resolveMatrixGridSettings,
  toMatrixGridSearch,
} from '@/lib/matrix-grid-search';
import type { NoteViewer } from '@/lib/notes-queries';
import { useDeriveMatrix } from '@/lib/use-derive-matrix';
import type { MatrixFolder, MatrixSummary } from '@mss_creator/shared';
import { Button, Icon } from '@pms-digital/design-system';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState } from 'react';

export const DUPLICATE_MATRIX_ERROR_MESSAGE = 'La duplication a échoué. Réessayez.';
export const MOVE_MATRIX_ERROR_MESSAGE = 'Le déplacement a échoué. Réessayez.';

/** How long a fresh copy stays highlighted. */
export const COPY_HIGHLIGHT_MS = 2500;

/** How long « Déplacée dans … » stays up with its « Annuler ». */
export const MOVE_NOTICE_MS = 8000;

export interface MatricesTabProps {
  companyId: string;
  /** Named in the notes panel (« Visible par … ») and in the folder dialog. */
  companyName: string;
  /** The company's setup is complete: a blank matrix may be created. */
  canCreate: boolean;
  /** The tab's URL search params, already parsed. */
  search: MatrixGridSearch;
  onSearchChange: (next: MatrixGridSearch) => void;
  /** For « Créée par : Moi »; undefined while the user loads. */
  currentUserId: string | undefined;
  /** Who writes the notes of a folder; undefined while the user loads (no notes panel then). */
  viewer?: NoteViewer | undefined;
}

/** A move just done, which « Annuler » can take back. */
interface MoveNotice {
  matrixId: string;
  previousFolderId: string | null;
  folderName: string | null;
}

/**
 * The Matrices tab of a company (issues #16 and #19, « Grille de matrices »
 * and « Dossiers de matrices »): the toolbar, the chips, the folders column,
 * and in the chosen view the grid of cards or the list. The actions of each
 * « ⋯ » menu are duplicate (stay on the grid, the copy shows first and glows,
 * or a notice when a filter hides it), create from (to the wizard), move to a
 * folder (a notice with « Annuler »), delete (confirmation dialog).
 *
 * The folder view (`dossier` in the URL) is navigation, not a filter: it sits
 * outside « Réinitialiser » and filters the matrices before the other filters.
 */
export function MatricesTab({
  companyId,
  companyName,
  canCreate,
  search,
  onSearchChange,
  currentUserId,
  viewer,
}: MatricesTabProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const settings = resolveMatrixGridSettings(search);
  const [deleteTarget, setDeleteTarget] = useState<MatrixSummary | null>(null);
  const [hiddenCopy, setHiddenCopy] = useState<MatrixSummary | null>(null);
  const [moveNotice, setMoveNotice] = useState<MoveNotice | null>(null);
  const [duplicateFailed, setDuplicateFailed] = useState(false);
  const [moveFailed, setMoveFailed] = useState(false);
  const [highlightedId, setHighlightedId] = useState<string | null>(null);
  const [folderDialog, setFolderDialog] = useState<FolderDialogMode | null>(null);
  // « Nouveau dossier… » of a « Déplacer vers »: the matrix to put in the new folder.
  const [matrixForNewFolder, setMatrixForNewFolder] = useState<MatrixSummary | null>(null);
  const [folderToDelete, setFolderToDelete] = useState<MatrixFolder | null>(null);
  // The folder whose notes panel / description editor is open; changing folder closes them.
  const [notesFolderId, setNotesFolderId] = useState<string | null>(null);
  const [editingDescriptionId, setEditingDescriptionId] = useState<string | null>(null);
  const highlightTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const moveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const derive = useDeriveMatrix();

  useEffect(
    () => () => {
      if (highlightTimer.current) clearTimeout(highlightTimer.current);
      if (moveTimer.current) clearTimeout(moveTimer.current);
    },
    [],
  );

  const matricesQuery = useQuery({
    queryKey: ['matrices', companyId],
    queryFn: () => matricesApi.listForCompany(companyId),
  });
  const foldersQuery = useFolders(companyId);
  const folders = foldersQuery.data ?? [];
  const folderNames = useMemo(
    () => new Map((foldersQuery.data ?? []).map((folder) => [folder.id, folder.name])),
    [foldersQuery.data],
  );

  // A folder is only a view once it is known: while the folders load, when
  // their request fails, or for an id that does not exist (any more), every
  // matrix shows (« Toutes les matrices ») rather than an empty grid.
  const requested = settings.dossier;
  const selectedFolder = isFolderId(requested)
    ? folders.find((folder) => folder.id === requested)
    : undefined;
  const selection: FolderSelection =
    isFolderId(requested) && !selectedFolder ? ALL_FOLDERS : requested;
  const viewSettings: MatrixGridSettings = { ...settings, dossier: selection };

  const createMatrixMutation = useMutation({
    mutationFn: () =>
      matricesApi.create({ companyId, ...(selectedFolder ? { folderId: selectedFolder.id } : {}) }),
    onSuccess: (matrix) => {
      void navigate({ to: '/wizard/$matrixId', params: { matrixId: matrix.id } });
    },
  });

  const moveMutation = useMoveMatrix(companyId);

  const duplicateMutation = useMutation({
    mutationFn: (id: string) => matricesApi.duplicate(id),
    onSuccess: (copy) => {
      queryClient.setQueryData<MatrixSummary[]>(['matrices', companyId], (previous) => [
        copy,
        ...(previous ?? []).filter((entry) => entry.id !== copy.id),
      ]);
      void queryClient.invalidateQueries({ queryKey: ['matrices', companyId] });
      void queryClient.invalidateQueries({ queryKey: ['folders', companyId] });
      setMoveNotice(null);
      if (matchesFilters(copy, viewSettings, currentUserId)) {
        setHiddenCopy(null);
        setHighlightedId(copy.id);
        if (highlightTimer.current) clearTimeout(highlightTimer.current);
        highlightTimer.current = setTimeout(() => setHighlightedId(null), COPY_HIGHLIGHT_MS);
      } else {
        setHiddenCopy(copy);
      }
    },
  });

  function apply(next: MatrixGridSettings) {
    onSearchChange(toMatrixGridSearch(next));
  }

  function selectSort(sort: MatrixGridSort) {
    if (sort === settings.sort) return;
    apply({ ...settings, sort, dir: DEFAULT_SORT_DIRECTIONS[sort] });
  }

  function toggleDirection() {
    apply({ ...settings, dir: settings.dir === 'asc' ? 'desc' : 'asc' });
  }

  function resetFilters() {
    apply(clearFilters(settings));
  }

  function selectFolder(next: FolderSelection) {
    apply({ ...settings, dossier: next });
  }

  async function duplicate(matrix: MatrixSummary): Promise<void> {
    setDuplicateFailed(false);
    setMoveFailed(false);
    derive.reset();
    try {
      await duplicateMutation.mutateAsync(matrix.id);
    } catch {
      // Reported under the toolbar; the menu only needs the promise to settle.
      setDuplicateFailed(true);
    }
  }

  async function deriveFrom(matrix: MatrixSummary): Promise<void> {
    setDuplicateFailed(false);
    setMoveFailed(false);
    await derive.deriveMatrix(matrix.id);
  }

  function showMoveNotice(notice: MoveNotice) {
    setHiddenCopy(null);
    setMoveNotice(notice);
    if (moveTimer.current) clearTimeout(moveTimer.current);
    moveTimer.current = setTimeout(() => setMoveNotice(null), MOVE_NOTICE_MS);
  }

  async function moveMatrix(
    matrix: MatrixSummary,
    folderId: string | null,
    folderName: string | null,
  ): Promise<void> {
    if (matrix.folderId === folderId) return;
    setMoveFailed(false);
    setDuplicateFailed(false);
    try {
      await moveMutation.mutateAsync({ matrixId: matrix.id, folderId });
      showMoveNotice({ matrixId: matrix.id, previousFolderId: matrix.folderId, folderName });
    } catch {
      // Reported under the toolbar; the menu only needs the promise to settle.
      setMoveFailed(true);
    }
  }

  function moveToFolder(matrix: MatrixSummary, folderId: string | null): Promise<void> {
    return moveMatrix(
      matrix,
      folderId,
      folderId === null ? null : (folderNames.get(folderId) ?? ''),
    );
  }

  function dropOnFolder(matrixId: string, folderId: string | null) {
    const matrix = (matricesQuery.data ?? []).find((entry) => entry.id === matrixId);
    if (matrix) void moveToFolder(matrix, folderId);
  }

  function undoMove(notice: MoveNotice) {
    if (moveTimer.current) clearTimeout(moveTimer.current);
    setMoveNotice(null);
    setMoveFailed(false);
    moveMutation.mutate(
      { matrixId: notice.matrixId, folderId: notice.previousFolderId },
      { onError: () => setMoveFailed(true) },
    );
  }

  function openCreateFolder(forMatrix: MatrixSummary | null = null) {
    setMatrixForNewFolder(forMatrix);
    setFolderDialog({ type: 'create' });
  }

  function closeFolderDialog() {
    setFolderDialog(null);
    setMatrixForNewFolder(null);
  }

  function folderSaved(folder: MatrixFolder) {
    const creating = folderDialog?.type === 'create';
    const matrix = matrixForNewFolder;
    closeFolderDialog();
    // The folder just created opens; a renamed one stays as it is.
    if (creating) selectFolder(folder.id);
    if (creating && matrix) void moveMatrix(matrix, folder.id, folder.name);
  }

  const all = matricesQuery.data ?? [];
  const inView = all.filter((matrix) => matchesFolder(matrix, selection));
  const visible = visibleMatrices(all, viewSettings, currentUserId);
  const filtering = hasActiveFilters(settings);
  // Not while loading: an empty list then says nothing about the filters.
  const filteredEmpty = filtering && matricesQuery.isSuccess && visible.length === 0;
  const folderEmpty =
    selectedFolder !== undefined && !filtering && matricesQuery.isSuccess && inView.length === 0;
  const looseCount = all.filter((matrix) => matrix.folderId === null).length;
  const notesOpen = selectedFolder !== undefined && notesFolderId === selectedFolder.id;
  const showFolderNames = selection === ALL_FOLDERS;

  function renderActions(matrix: MatrixSummary) {
    return (
      <MatrixActionsMenu
        matrix={matrix}
        folders={folders}
        onDuplicate={duplicate}
        onDerive={deriveFrom}
        onMove={moveToFolder}
        onNewFolder={openCreateFolder}
        onDelete={setDeleteTarget}
      />
    );
  }

  const errorMessage = duplicateFailed
    ? DUPLICATE_MATRIX_ERROR_MESSAGE
    : moveFailed
      ? MOVE_MATRIX_ERROR_MESSAGE
      : derive.errorMessage;

  const newMatrixButtonDisabled = !canCreate || createMatrixMutation.isPending;

  return (
    <div className="space-y-3">
      <MatrixGridToolbar
        settings={settings}
        onChange={apply}
        onSortChange={selectSort}
        onToggleDirection={toggleDirection}
        onNewFolder={() => openCreateFolder()}
      />
      <FilterChips
        settings={settings}
        onChange={apply}
        onReset={resetFilters}
        counts={matricesQuery.isSuccess ? { visible: visible.length, total: inView.length } : null}
      />

      {errorMessage && (
        <p
          role="alert"
          className="flex items-center gap-2 rounded-md bg-danger-tint px-3 py-2 font-semibold text-rouge-deep text-small"
        >
          <Icon name="error" size={16} />
          {errorMessage}
        </p>
      )}
      {hiddenCopy && (
        // biome-ignore lint/a11y/useSemanticElements: a live notice, not a form output
        <div
          role="status"
          className="flex items-center gap-3 rounded-md border-2 border-info bg-info-tint px-4 py-2 text-small"
        >
          <span>Copie créée : « {matrixTitle(hiddenCopy)} »</span>
          <Link
            to={matrixLinkTarget(hiddenCopy)}
            params={{ matrixId: hiddenCopy.id }}
            className="font-semibold text-bleu-nuit underline underline-offset-2"
          >
            Ouvrir
          </Link>
          <button
            type="button"
            aria-label="Fermer le message"
            onClick={() => setHiddenCopy(null)}
            className="ml-auto rounded-sm p-0 text-gray-600 hover:bg-white hover:text-noir"
          >
            <Icon name="close" size={16} />
          </button>
        </div>
      )}
      {moveNotice && (
        // biome-ignore lint/a11y/useSemanticElements: a live notice, not a form output
        <div
          role="status"
          className="flex items-center gap-3 rounded-md border-2 border-info bg-info-tint px-4 py-2 text-small"
        >
          <span>{movedMessage(moveNotice.folderName)}</span>
          <button
            type="button"
            onClick={() => undoMove(moveNotice)}
            className="font-semibold text-bleu-nuit underline underline-offset-2"
          >
            Annuler
          </button>
          <button
            type="button"
            aria-label="Fermer le message"
            onClick={() => setMoveNotice(null)}
            className="ml-auto rounded-sm p-0 text-gray-600 hover:bg-white hover:text-noir"
          >
            <Icon name="close" size={16} />
          </button>
        </div>
      )}

      {matricesQuery.isError && (
        <p className="text-danger text-small">Les matrices n'ont pas pu être chargées.</p>
      )}

      <div className="flex flex-col gap-4 lg:flex-row">
        <FolderSidebar
          folders={folders}
          totalCount={all.length}
          looseCount={looseCount}
          selection={selection}
          onSelect={selectFolder}
          onNewFolder={() => openCreateFolder()}
          onDropMatrix={dropOnFolder}
          loadFailed={foldersQuery.isError}
          onRetry={() => void foldersQuery.refetch()}
        />

        <div className="min-w-0 flex-1">
          {selectedFolder ? (
            <div className="mb-3">
              <FolderHeader
                folder={selectedFolder}
                notesOpen={notesOpen}
                onToggleNotes={() => setNotesFolderId(notesOpen ? null : selectedFolder.id)}
                onRename={(folder) => setFolderDialog({ type: 'rename', folder })}
                onEditDescription={() => setEditingDescriptionId(selectedFolder.id)}
                onDelete={setFolderToDelete}
              />
              <FolderDescription
                folder={selectedFolder}
                companyId={companyId}
                editing={editingDescriptionId === selectedFolder.id}
                onEditingChange={(editing) =>
                  setEditingDescriptionId(editing ? selectedFolder.id : null)
                }
              />
            </div>
          ) : (
            <h2 className="mb-3 flex items-center gap-2 font-semibold text-small">
              {selection === NO_FOLDER ? (
                <Icon name="description" size={16} />
              ) : (
                <Icon name="grid_view" size={16} />
              )}
              {selection === NO_FOLDER ? 'Hors dossier' : 'Toutes les matrices'}
              {matricesQuery.isSuccess && (
                <span className="font-normal text-caption text-gray-500">{inView.length}</span>
              )}
            </h2>
          )}

          <div className="flex flex-col items-start gap-4 xl:flex-row">
            <div className="w-full min-w-0 flex-1 space-y-3">
              {settings.view === 'list' ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600 text-small">
                      {visible.length} {visible.length > 1 ? 'matrices' : 'matrice'}
                    </span>
                    <Button
                      variant="secondary"
                      type="button"
                      size="sm"
                      disabled={newMatrixButtonDisabled}
                      onClick={() => createMatrixMutation.mutate()}
                    >
                      <Icon name="add" size={16} />
                      Nouvelle matrice
                    </Button>
                  </div>
                  {visible.length > 0 && (
                    <MatrixListTable
                      matrices={visible}
                      sort={settings.sort}
                      dir={settings.dir}
                      onHeaderSort={(sort) =>
                        sort === settings.sort ? toggleDirection() : selectSort(sort)
                      }
                      renderActions={renderActions}
                      highlightedId={highlightedId}
                      folderNames={showFolderNames ? folderNames : null}
                    />
                  )}
                </div>
              ) : (
                // As many columns of 260 px or more as the width allows; the notes
                // panel narrows the grid, so it simply holds fewer columns.
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-[repeat(auto-fill,minmax(260px,1fr))]">
                  <NewMatrixCard
                    disabled={!canCreate}
                    onClick={() => createMatrixMutation.mutate()}
                  />
                  {visible.map((matrix) => (
                    <MatrixCard
                      key={matrix.id}
                      matrix={matrix}
                      highlighted={matrix.id === highlightedId}
                      actions={renderActions(matrix)}
                      folderName={
                        showFolderNames && matrix.folderId !== null
                          ? (folderNames.get(matrix.folderId) ?? null)
                          : null
                      }
                    />
                  ))}
                </div>
              )}
              {filteredEmpty && (
                <FilteredEmptyState
                  onReset={resetFilters}
                  {...(selection !== ALL_FOLDERS
                    ? { onSearchEverywhere: () => selectFolder(ALL_FOLDERS) }
                    : {})}
                />
              )}
              {folderEmpty && <FolderEmptyState />}
            </div>

            {notesOpen && selectedFolder && viewer && (
              <FolderNotesPanel
                folder={selectedFolder}
                companyId={companyId}
                companyName={companyName}
                viewer={viewer}
                onClose={() => setNotesFolderId(null)}
              />
            )}
          </div>
        </div>
      </div>

      <DeleteMatrixDialog
        matrix={deleteTarget}
        companyId={companyId}
        onClose={() => setDeleteTarget(null)}
      />
      <FolderDialog
        mode={folderDialog}
        companyId={companyId}
        companyName={companyName}
        onClose={closeFolderDialog}
        onSaved={folderSaved}
      />
      <DeleteFolderDialog
        folder={folderToDelete}
        companyId={companyId}
        onClose={() => setFolderToDelete(null)}
        onDeleted={() => {
          setFolderToDelete(null);
          selectFolder(ALL_FOLDERS);
        }}
      />
    </div>
  );
}
